import { memo, useCallback, useMemo, useState } from "react";
import type { FormEvent } from "react";

import { useLocalization } from "@fluent/react";
import { Button, DateInput, Row } from "@narmi/design_system";
import { format, isValid, parse } from "date-fns";

import {
  DATE_PRESETS,
  LIVE_LEDGER_DATE_FROM_QUERY_KEY,
  LIVE_LEDGER_DATE_TO_QUERY_KEY,
  toLocalYmd,
} from "../../constants";
import { findMatchingPreset, getLocaleDateFormat } from "../../helpers";
import { useRangeFilter } from "../../hooks/useRangeFilter";

import styles from "./DateRangeFilterPanel.module.scss";

import type { DatePreset } from "../../constants";

export interface DateRangeFilterPanelProps {
  close: () => void;
  onApply: () => void;
}

const MemoizedDateInput = memo(DateInput) as typeof DateInput;

function isCompleteDate(value: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(value);
}

function parseYmd(value: string): Date | undefined {
  if (!isCompleteDate(value)) return undefined;
  const [y, m, d] = value.split("-").map(Number);
  return new Date(y, m - 1, d);
}

interface FrozenPreset extends DatePreset {
  frozenMin: string;
  frozenMax: string;
}

const parseDate = (raw: string): string | undefined => {
  return isCompleteDate(raw) ? raw : undefined;
};

const DateRangeFilterPanel = ({
  close,
  onApply,
}: DateRangeFilterPanelProps) => {
  const { l10n } = useLocalization();

  const today = useMemo(() => toLocalYmd(new Date()), []);
  const localeDateFormat = useMemo(() => getLocaleDateFormat(), []);

  // Freeze preset dates at mount time so all comparisons within a single
  // panel session use the same values, even if the calendar date rolls over.
  const frozenPresets = useMemo<FrozenPreset[]>(
    () =>
      DATE_PRESETS.map((p) => ({
        ...p,
        frozenMin: p.minDate(),
        frozenMax: p.maxDate(),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const {
    min: urlFrom,
    max: urlTo,
    setRange,
    clearRange,
  } = useRangeFilter<string>({
    queryKeyMin: LIVE_LEDGER_DATE_FROM_QUERY_KEY,
    queryKeyMax: LIVE_LEDGER_DATE_TO_QUERY_KEY,
    parseValue: parseDate,
    serializeValue: (v) => v,
    formatLabel: () => undefined,
  });

  const initialPreset = findMatchingPreset(
    urlFrom,
    urlTo,
    frozenPresets,
    (p) => ({ min: p.frozenMin, max: p.frozenMax })
  );

  const [draftPreset, setDraftPreset] = useState<FrozenPreset | null>(
    urlFrom && urlTo && !initialPreset ? null : initialPreset
  );
  const [isCustom, setIsCustom] = useState(
    urlFrom && urlTo ? initialPreset === null : false
  );
  const [draftStart, setDraftStart] = useState(urlFrom ?? "");
  const [draftEnd, setDraftEnd] = useState(urlTo ?? "");
  const [inputVersion, setInputVersion] = useState(0);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const startDefaultDate = useMemo(() => parseYmd(draftStart), [inputVersion]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const endDefaultDate = useMemo(() => parseYmd(draftEnd), [inputVersion]);

  const hasCompleteDates =
    isCompleteDate(draftStart) && isCompleteDate(draftEnd);
  const hasDateRangeError = hasCompleteDates && draftStart > draftEnd;
  const hasFutureDateError =
    (isCompleteDate(draftStart) && draftStart > today) ||
    (isCompleteDate(draftEnd) && draftEnd > today);

  const handlePresetClick = (preset: FrozenPreset) => {
    setDraftPreset(preset);
    setIsCustom(false);
    setDraftStart(preset.frozenMin);
    setDraftEnd(preset.frozenMax);
    setInputVersion((v) => v + 1);
  };

  const handleCustomClick = () => {
    setDraftPreset(null);
    setIsCustom(true);
  };

  const handleStartChange = useCallback((value: string) => {
    setDraftStart(value);
    setDraftPreset(null);
    setIsCustom(true);
  }, []);

  const handleEndChange = useCallback((value: string) => {
    setDraftEnd(value);
    setDraftPreset(null);
    setIsCustom(true);
  }, []);

  // dateFormat shows locale text (e.g. "10/05/2026") in the input, but our
  // state needs YYYY-MM-DD. This converts display text to ISO; after a
  // calendar pick it produces the same value onChange already sent.
  const dateFnsFormat = useMemo(
    () =>
      localeDateFormat
        .replace("Y", "yyyy")
        .replace("m", "MM")
        .replace("d", "dd"),
    [localeDateFormat]
  );

  const fromDisplayDate = useCallback(
    (text: string): string => {
      const parsed = parse(text, dateFnsFormat, new Date());
      return isValid(parsed) && parsed.getFullYear() >= 1000
        ? format(parsed, "yyyy-MM-dd")
        : text;
    },
    [dateFnsFormat]
  );

  // Fallback for flatpickr swallowing onChange on outside clicks
  // (e.g. clicking Apply while focus is still in the date input).
  const handleStartInput = useCallback(
    (event: FormEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      handleStartChange(fromDisplayDate(event.currentTarget.value));
    },
    [handleStartChange, fromDisplayDate]
  );

  const handleEndInput = useCallback(
    (event: FormEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      handleEndChange(fromDisplayDate(event.currentTarget.value));
    },
    [handleEndChange, fromDisplayDate]
  );

  const handleApply = () => {
    const hasDraftDates = draftStart !== "" || draftEnd !== "";
    if (
      isCustom &&
      hasDraftDates &&
      (!hasCompleteDates || hasDateRangeError || hasFutureDateError)
    ) {
      return;
    }
    setRange(draftStart || undefined, draftEnd || undefined);
    onApply();
    close();
  };

  const handleRemove = () => {
    clearRange();
    onApply();
    close();
  };

  return (
    <div
      className={styles["date-range-filter-panel"]}
      data-testid="date-range-filter-panel"
    >
      <span className={styles["date-range-filter-panel-heading"]}>
        {l10n.getString("live-ledger-filter-date-presets")}
      </span>

      <div className={styles["date-range-filter-panel-options"]}>
        {frozenPresets.map((preset) => (
          <label
            key={preset.value}
            className={styles["date-range-filter-panel-radio-row"]}
          >
            <input
              type="radio"
              name="date-preset"
              value={preset.value}
              checked={draftPreset?.value === preset.value}
              onChange={() => handlePresetClick(preset)}
              className={styles["date-range-filter-panel-radio"]}
            />
            {l10n.getString(preset.ftlId)}
          </label>
        ))}
      </div>

      <div className={styles["date-range-filter-panel-preset-divider"]} />

      <label className={styles["date-range-filter-panel-radio-row"]}>
        <input
          type="radio"
          name="date-preset"
          value="custom"
          checked={isCustom}
          onChange={handleCustomClick}
          className={styles["date-range-filter-panel-radio"]}
        />
        {l10n.getString("live-ledger-filter-date-custom")}
      </label>

      <div className={styles["date-range-filter-panel-custom-section"]}>
        <div className={styles["date-range-filter-panel-date-inputs"]}>
          <MemoizedDateInput
            key={`start-${inputVersion}`}
            label={l10n.getString("live-ledger-filter-date-start")}
            defaultDate={startDefaultDate as unknown as string}
            onChange={handleStartChange}
            onInput={handleStartInput}
            type="text"
            disablePortal
            testId="date-filter-start"
            dateFormat={localeDateFormat}
          />
          <MemoizedDateInput
            key={`end-${inputVersion}`}
            label={l10n.getString("live-ledger-filter-date-end")}
            defaultDate={endDefaultDate as unknown as string}
            onChange={handleEndChange}
            onInput={handleEndInput}
            type="text"
            disablePortal
            testId="date-filter-end"
            dateFormat={localeDateFormat}
          />
        </div>

        {hasDateRangeError && (
          <div
            className={styles["date-range-filter-panel-error"]}
            data-testid="date-filter-error"
            role="alert"
          >
            {l10n.getString("live-ledger-filter-date-error-range")}
          </div>
        )}
        {!hasDateRangeError && hasFutureDateError && (
          <div
            className={styles["date-range-filter-panel-error"]}
            data-testid="date-filter-error"
            role="alert"
          >
            {l10n.getString("live-ledger-filter-date-error-future")}
          </div>
        )}

        <div className={styles["date-range-filter-panel-divider"]} />

        <div className={styles["date-range-filter-panel-actions"]}>
          <Row justifyContent="space-between" alignItems="center">
            <Button
              kind="plain"
              size="s"
              label={l10n.getString("live-ledger-filter-date-remove")}
              onClick={handleRemove}
              type="button"
              testId="date-filter-remove"
            />
            <Button
              kind="primary"
              size="s"
              label={l10n.getString("live-ledger-filter-date-apply")}
              onClick={handleApply}
              testId="date-filter-apply"
            />
          </Row>
        </div>
      </div>
    </div>
  );
};

export default DateRangeFilterPanel;
